import { Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import './DriverApp.css';

const FEATURES = [
  {
    tag: 'Navigation',
    title: 'Smart Turn-by-Turn Navigation',
    desc: 'Built-in navigation guides drivers on the fastest route, updated live with traffic conditions. No need to switch apps — everything is inside Route Rover.',
    points: [
      'Live traffic-aware routing',
      'Multi-stop trip sequencing',
      'Real-time ETA sent to customers',
      'One-tap directions to pickup & drop-off',
    ],
    color: '#0057FF',
    img: '/app-screen3.webp',
  },
  {
    tag: 'Driver Profile',
    title: 'Full Shift & Status Control',
    desc: 'Drivers manage their availability, shifts, and queue from one screen. Go on break, end shift, or check incoming orders — all without leaving the app.',
    points: [
      'Live availability toggle',
      'Queue management',
      'Break & shift controls',
      'Store assignment display',
    ],
    color: '#00D4FF',
    img: '/app-screen2.webp',
    reverse: true,
  },
  {
    tag: 'Performance',
    title: 'Incentive-Based Earnings System',
    desc: "Drivers are rewarded based on delivery performance. Excellent on-time deliveries earn bonuses, while delays are flagged transparently so drivers can improve.",
    points: [
      'Excellent, Good, Late, Abuse ratings',
      'Real-time earnings per trip',
      'Performance-linked incentives',
      'Full transparency on deductions',
    ],
    color: '#0057FF',
    img: '/app-screen1.webp',
  },
  {
    tag: 'Schedule',
    title: 'Shift Scheduling at a Glance',
    desc: "Drivers see today's, tomorrow's, and upcoming shifts all in one place. No confusion, no missed shifts — always know when you're on.",
    points: [
      'Today & upcoming shift view',
      'Shift start and end times',
      'Color-coded shift status',
      'Push reminders before shifts',
    ],
    color: '#00D4FF',
    img: '/app-screen4.webp',
    reverse: true,
  },
];

const STEPS = [
  { num: '01', title: 'Download the App', desc: 'Available on iOS and Android. Free to download and get started instantly.' },
  { num: '02', title: 'Complete Your Profile', desc: 'Add your vehicle info, upload your license, and verify your identity in minutes.' },
  { num: '03', title: 'Get Approved', desc: 'Our team reviews your application within 24 hours and activates your account.' },
  { num: '04', title: 'Start Delivering', desc: 'Go online, accept orders, and start earning. Full support available from day one.' },
];

export default function DriverApp() {
  const { lang } = useLang();
  const isAr = lang === 'ar';

  return (
    <main className="driver-page" dir={isAr ? 'rtl' : 'ltr'}>

      {/* HERO with splash */}
      <section className="driver-hero">
        <div className="container driver-hero__inner">
          <div className="driver-hero__text">
            <p className="section-label">Rider App</p>
            <h1>The App Built for<br /><span className="gradient-text">Every Driver</span></h1>
            <p>Route Rover's Rider App gives drivers everything they need to deliver faster, earn more, and stay in control — all from one powerful mobile app.</p>
            </div>
        </div>
      </section>

      {/* FEATURES */}
      {FEATURES.map((f, i) => (
        <section key={i} className={`feat-section${f.reverse ? ' feat-section--reverse' : ''}`}>
          <div className="container feat-section__inner">
            <div className="feat-section__text">
              <span className="feat-tag" style={{ color: f.color, borderColor: f.color }}>{f.tag}</span>
              <h2>{f.title}</h2>
              <p>{f.desc}</p>
              <ul className="feat-points">
                {f.points.map((p, j) => (
                  <li key={j}>
                    <span className="feat-check" style={{ color: f.color }}>✓</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="feat-section__visual driver-screen-wrap">
              <img src={f.img} alt={f.title} className="driver-screen-img" />
            </div>
          </div>
        </section>
      ))}

{/* CTA */}
      <section className="driver-cta">
        <div className="container">
          <h2>Ready to Start Delivering<br />with Route Rover?</h2>
          <p>Join thousands of drivers already earning with the smartest delivery platform in the region.</p>
          <Link to="/contact" className="cta-primary cta-primary--large">Apply Now</Link>
        </div>
      </section>

    </main>
  );
}
